import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance, plainAxios } from "../../../../../Common/Interceptors/AxiosConfig";

const initialState = {
  errors: null,
  isLoading: false,
  // Review submission
  submittedReview: null,
  // Subservice reviews
  serviceReviews: [],
  servicePagination: null,
  // Service provider rating
  serviceProviderRating: null,
  ratingFetchedAt: null, // Track when rating was fetched
  ratingServiceId: null, // Track which serviceId was fetched
  // Review eligibility check
  reviewEligibility: null,
  // Subservice rating
  serviceRating: null,
};

const ReviewSlice = createSlice({
  name: "review",
  initialState,
  reducers: {
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setSubmittedReview: (state, action) => {
      state.submittedReview = action.payload;
    },
    setServiceReviews: (state, action) => {
      state.serviceReviews = action.payload;
    },
    setServicePagination: (state, action) => {
      state.servicePagination = action.payload;
    },
    setServiceProviderRating: (state, action) => {
      state.serviceProviderRating = action.payload.rating;
      state.ratingFetchedAt = action.payload.fetchedAt;
      state.ratingServiceId = action.payload.serviceId;
    },
    setReviewEligibility: (state, action) => {
      state.reviewEligibility = action.payload;
    },
    setServiceRating: (state, action) => {
      state.serviceRating = action.payload;
    },
    clearReviewData: (state) => {
      state.submittedReview = null;
      state.serviceReviews = [];
      state.servicePagination = null;
      state.reviewEligibility = null;
    },
  },
});

// Submit a review
export const submitReview = (reviewData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = "reviews";
    const response = await axiosInstance.post(URL, reviewData);
    const { status, data } = response?.data || {};
    if (status === "success") {
      dispatch(setSubmittedReview(data));
    }
    dispatch(setLoading(false));
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message || error.message || "Failed to submit review";
    dispatch(setLoading(false));
    dispatch(setErrors({ errorMsg }));
  }
};

// Get reviews for a subservice
export const getServiceReviews =
  (serviceId, page = 1, limit = 10) =>
  async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const URL = `reviews/business/${serviceId}?page=${page}&limit=${limit}`;
      const response = await axiosInstance.get(URL);
      const { status, data } = response?.data || {};
      if (status === "success") {
        dispatch(setServiceReviews(data.reviews || []));
        dispatch(setServicePagination(data.pagination || null));
      }
      dispatch(setLoading(false));
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch subservice reviews";
      dispatch(setLoading(false));
      dispatch(setErrors({ errorMsg }));
    }
  };

// Get subservice rating statistics
export const getServiceRating = (serviceId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `reviews/business/${serviceId}`;
    const response = await axiosInstance.get(URL);
    const { status, data } = response?.data || {};
    if (status === "success") {
      dispatch(setServiceRating(data));
    }
    dispatch(setLoading(false));
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to fetch subservice rating";
    dispatch(setLoading(false));
    dispatch(setErrors({ errorMsg }));
  }
};

// Get service provider rating statistics
// CACHE_DURATION: 5 minutes (300000 ms)
const CACHE_DURATION = 5 * 60 * 1000;

export const getServiceProviderRating = (serviceId) => async (dispatch, getState) => {
  if (!serviceId) return;

  const state = getState().review;
  const now = Date.now();

  // Prevent concurrent requests - if already loading, skip
  if (state.isLoading) {
    return;
  }

  // Check cache: if same serviceId and fetched within cache duration, skip API call
  if (
    state.ratingServiceId === serviceId &&
    state.ratingFetchedAt &&
    (now - state.ratingFetchedAt) < CACHE_DURATION
  ) {
    return; // Use cached data
  }

  dispatch(setLoading(true));
  try {
    const URL = `reviews/business/${serviceId}`;
    const response = await plainAxios.get(URL);
    const { status, data } = response?.data || {};
    if (status === "success") {
      dispatch(setServiceProviderRating({
        rating: data,
        fetchedAt: now,
        serviceId: serviceId
      }));
    }
    dispatch(setLoading(false));
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to fetch service provider rating";
    dispatch(setLoading(false));
    dispatch(setErrors({ errorMsg }));
  }
};

// Check if booking is eligible for review
export const checkReviewEligibility = (bookingId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `reviews/booking/${bookingId}/check`;
    const response = await axiosInstance.get(URL);
    const { status, data } = response?.data || {};
    if (status === "success") {
      dispatch(setReviewEligibility(data));
    }
    dispatch(setLoading(false));
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to check review eligibility";
    dispatch(setLoading(false));
    dispatch(setErrors({ errorMsg }));
  }
};

export const {
  setErrors,
  setLoading,
  setSubmittedReview,
  setServiceReviews,
  setServicePagination,
  setServiceProviderRating,
  setReviewEligibility,
  setServiceRating,
  clearReviewData,
} = ReviewSlice.actions;

export default ReviewSlice.reducer;
